import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";

export default function ResendVerification() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const handleResend = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setError(t("resend.error.tooMany"));
        } else {
          setError(data.detail || t("resend.error.failed"));
        }
        return;
      }

      setSent(true);
      setMessage(t("resend.success"));
      // Auto-redirect to home after 3 seconds
      setTimeout(() => navigate("/", { replace: true }), 3000);
    } catch (err) {
      setError(t("resend.error.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <button
          onClick={() => navigate("/", { replace: true })}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">{t("resend.back")}</span>
        </button>

        {/* Success State */}
        {sent ? (
          <div className="text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 rounded-full border-4 border-green-200 flex items-center justify-center bg-white">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {t("resend.sent.title")}
            </h1>
            <p className="text-slate-600 mb-2">{t("resend.sent.desc")}</p>
            <p className="font-medium text-slate-900 mb-6">{email}</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900">{t("resend.sent.note")}</p>
            </div>
            <p className="text-sm text-slate-500">
              {t("resend.sent.redirecting")}
            </p>
          </div>
        ) : (
          <div>
            {/* Title */}
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {t("resend.title")}
            </h1>
            <p className="text-slate-600 mb-8">{t("resend.subtitle")}</p>

            {/* Form */}
            <form onSubmit={handleResend} className="space-y-4">
              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t("resend.email.label")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nama@example.com"
                  required
                  disabled={loading}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:cursor-not-allowed transition"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Message */}
              {message && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">{message}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? t("resend.submitting") : t("resend.submit")}
              </button>
            </form>

            {/* Help Text */}
            <div className="mt-6 text-center text-sm text-slate-600 space-y-2">
              <p>
                {t("resend.haveAccount")}{" "}
                <a
                  href="/login"
                  className="text-blue-600 hover:underline font-medium"
                >
                  {t("resend.signIn")}
                </a>
              </p>
              <p>
                {t("resend.noAccount")}{" "}
                <a
                  href="/register"
                  className="text-blue-600 hover:underline font-medium"
                >
                  {t("resend.register")}
                </a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
